import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog"
import Grid from "@mui/material/Grid";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { PracticeLocationInfo, PracticeProviderInfo } from "../../../redux/slice/productAgreement/createAgreementSlice";
import { useCommonReducer } from "../../../redux/slice/commonSlice";
import { FormControl, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import Table from "../../common/CustomTable";
import { tokens } from "../../../theme";
import { useDispatch } from "react-redux";
import { fetchPracticeByIdData } from "../../../redux/slice/practice/practiceEditSlice";
import CustomRadioButton from "../../common/CustomRadioButton";
import CloseIcon from "@mui/icons-material/Close";
import { Building2 } from "lucide-react";
import { useModalStyles } from "../../common/useModalStyles";

interface PracticeDetailsPopup {
    open: boolean;
    onClose: () => void;
    practiceId: string | null
};

interface Column {
    id: string;
    label: string;
    minWidth?: number;
    align?: "left" | "center" | "right";
    format?: (value: number) => string;
}

const locationColumns: Column[] = [
    { id: "id", label: "Practice Location Address", minWidth: 100 },
];

const providerColumns: Column[] = [
    { id: "name", label: "Provider Name", minWidth: 100 },
    { id: "npi", label: "Provider NPI", minWidth: 100 },
    { id: "ptan", label: "Provider PTAN", minWidth: 100 },
];


const PracticeDetailsPopup: React.FC<PracticeDetailsPopup> = ({
    open,
    onClose,
    practiceId
}) => {

    const { control, handleSubmit, reset, watch, getValues, setValue } = useForm();
    const [locationRows, setLocationRows] = React.useState<{ id: string }[]>([]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDark = theme.palette.mode === "dark";
    const {
        tokens: modalTokens,
        paperSx,
        headerSx,
        iconBoxSx,
        closeIconSx,
        bodySx,
        footerSx,
        cancelBtnSx,
    } = useModalStyles();
    const dispatch = useDispatch();
    const colors = tokens(theme.palette.mode);
    const [providerRows, setProviderRows] = React.useState<{ name: string, npi: string, ptan: string }[]>([]);
    const { practiceInfo } = useCommonReducer();


    useEffect(() => {

        //@ts-ignore
        practiceId && dispatch(fetchPracticeByIdData(practiceId)
        ).then((res: any) => {
            let practice = res.payload?.data;
            setLocations(practice);
            setProviders(practice);
            setValue("billingType",practice?.billingType);
        });
    }, [practiceId]);

    const setLocations = (practiceRow: any) => {
        const locationsArr: { id: string; }[] = [];
        practiceRow && practiceRow.practiceLocations?.forEach((location: PracticeLocationInfo) => {
            const locationParts = [location.streetAddress, location.unitNumber, location.city, location.state, location.zipCode];
            const filteredLocationParts = locationParts.filter(part => part !== undefined && part !== null);
            locationsArr.push({
                id: filteredLocationParts?.join(", ")
            });
        });
        setLocationRows(locationsArr);
    };

    const setProviders = (practiceRow: any) => {
        const providersArr: { name: string, npi: string, ptan: string }[] = [];
        practiceRow && practiceRow.practiceProviders?.forEach((provider: PracticeProviderInfo) => {
            providersArr.push({
                name: (provider.title ? provider.title + " " : "") + provider.firstName + " " + provider.middleName + " " + provider.lastName,
                npi: provider.npi,
                ptan: provider.ptan
            });
        });
        setProviderRows(providersArr);
    }

    return (
        <>
            <Dialog
                open={open}
                keepMounted
                onClose={onClose}
                fullWidth
                fullScreen={isMobile}
                scroll="paper"
                PaperProps={{ sx: paperSx(isMobile) }}
                aria-describedby="practice-details-dialog-description"
                onClick={(event) => {
                    event.stopPropagation();
                }}
            >
                <Box
                    component="form"
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                    }}
                >
                    <Box sx={headerSx}>
                        <Box sx={iconBoxSx(modalTokens.accent)}>
                            <Building2 size={20} color={modalTokens.accent} strokeWidth={1.8} />
                        </Box>
                        <Box sx={{ flex: 1, pr: 4 }}>
                            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: modalTokens.textPrimary, lineHeight: 1.3 }}>
                                Practice Details
                            </Typography>
                            <Typography id="practice-details-dialog-description" sx={{ fontSize: "0.8rem", color: modalTokens.textSecondary, mt: 0.15 }}>
                                Review billing mode, providers, and practice locations.
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
                            <CloseIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                    </Box>
                    <Box
                        sx={{
                            ...bodySx,
                            minHeight: 0,
                            overflowY: "auto",
                            overflowX: "hidden",
                            scrollbarGutter: "stable",
                            "&::-webkit-scrollbar": { width: 6, height: 6 },
                            "&::-webkit-scrollbar-track": { background: "transparent" },
                            "&::-webkit-scrollbar-thumb": {
                                background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                                borderRadius: 4,
                            },
                        }}
                    >
                        <Box p={1}>
                            <Grid container spacing={2}>
                                <Grid item md={12}>
                                    <p>
                                        How the practice bills?
                                    </p>
                                    <FormControl>
                                        <CustomRadioButton control={control} controllerName="billingType" options={[{ value: "INDIVIDUAL", label: "Individual NPI" }, { value: "GROUP", label: "Group NPI" }]} isDisabled={true}></CustomRadioButton>
                                    </FormControl>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                        <Table
                                            columns={providerColumns}
                                            rows={providerRows}
                                            disablePagination={true}
                                            showMenu={true} 
                                            borderBox = {true}/>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                    <Table
                                            columns={locationColumns}
                                            rows={locationRows}
                                            disablePagination={true} 
                                            showMenu={true}
                                            borderBox = {true}/>
                                            
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                    <Box sx={footerSx}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1.5,
                                flexDirection: { xs: "column", sm: "row" },
                            }}
                        >
                            <Box
                                component="button"
                                type="button"
                                onClick={onClose}
                                sx={{
                                    ...cancelBtnSx,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.8,
                                    px: 3,
                                    py: 1.2,
                                    border: "1px solid",
                                    cursor: "pointer",
                                    order: { xs: 2, sm: 1 },
                                }}
                            >
                                Cancel
                            </Box>
                        </Box>
                    </Box>
                </Box>

            </Dialog>
        </>
    )
}

export default PracticeDetailsPopup;